import type { PayloadAction } from '@reduxjs/toolkit';
import type { DivisionDto } from 'shared/dtos/divisions.dto';
import type { DivisionsState } from 'shared/interfaces/divisions.interface';

import { createSlice } from '@reduxjs/toolkit';

import {
  createDivisionThunk,
  getDivisionsThunk,
  getDivisionThunk,
  updateDivisionThunk,
} from './divisions.thunks';

const initialState: DivisionsState = {
  list: [],
  current: null,
  assigned: [],
  isCreateUpdateDivisionThunkPending: false,
};

const divisionsSlice = createSlice({
  name: 'divisions',
  initialState,
  reducers: {
    removeDivisionFromStore: (state) => {
      state.current = null;
    },
    setSelectedDivisions: (state, action: PayloadAction<number[]>) => {
      state.assigned = action.payload;
    },
    clearSelectedDivisions: (state) => {
      state.assigned = [];
    },
    setCurrentDivision: (state, action: PayloadAction<DivisionDto>) => {
      state.current = action.payload;
    },
    setDivisionMissions: (state, action) => {
      if (state.current) {
        state.current.missions = action.payload;
      }
    },
    setDivisionEnvironments: (state, action) => {
      if (state.current) {
        state.current.environments = action.payload;
      }
    },
    clearCurrentDivision: (state) => {
      state.current = null;
    },
    clearAgencyIdFromSelectedDivision: (
      state,
      action: PayloadAction<number>,
    ) => {
      const id = action.payload;

      state.list = state.list.map((division) => {
        if (division.id === id) {
          return {
            ...division,
            AgencyId: null,
            toUnclaim: true,
          };
        }
        return division;
      });
      state.assigned = state.assigned.filter((item) => item !== id);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDivisionsThunk.fulfilled, (state, action) => {
      state.list = action.payload.rows;
    });

    builder.addCase(getDivisionThunk.fulfilled, (state, action) => {
      state.current = action.payload;
    });

    builder
      .addCase(createDivisionThunk.pending, (state) => {
        state.isCreateUpdateDivisionThunkPending = true;
      })
      .addCase(createDivisionThunk.fulfilled, (state, action) => {
        state.current = action.payload;
        state.isCreateUpdateDivisionThunkPending = false;
      })
      .addCase(createDivisionThunk.rejected, (state) => {
        state.isCreateUpdateDivisionThunkPending = false;
      });

    builder
      .addCase(updateDivisionThunk.pending, (state) => {
        state.isCreateUpdateDivisionThunkPending = true;
      })
      .addCase(updateDivisionThunk.fulfilled, (state, action) => {
        state.current = action.payload;
        state.isCreateUpdateDivisionThunkPending = false;
      })
      .addCase(updateDivisionThunk.rejected, (state) => {
        state.isCreateUpdateDivisionThunkPending = false;
      });
  },
});

export const {
  removeDivisionFromStore,
  clearAgencyIdFromSelectedDivision,
  clearCurrentDivision,
  clearSelectedDivisions,
  setCurrentDivision,
  setDivisionEnvironments,
  setDivisionMissions,
  setSelectedDivisions,
} = divisionsSlice.actions;

export const divisionsReducer = divisionsSlice.reducer;
